import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from '@arm-web/ui-components';
import { ConfirmationService } from '../../services/confirmation.service';

@Component({
  selector: 'app-floating-confirmation',
  standalone: true,
  imports: [CommonModule, Button],
  templateUrl: './floating-confirmation.html',
  styleUrl: './floating-confirmation.css',
})
export class FloatingConfirmationComponent {
  // Handle Primary action (legacy/test support)
  handlePrimaryClick(): void {
    if (typeof this.confirmationService.onPrimaryClick === 'function') {
      this.confirmationService.onPrimaryClick();
    } else {
      this.handleNextClick();
    }
  }
  // Inject confirmation service
  private confirmationService = inject(ConfirmationService);

  // Inputs from service
  show = this.confirmationService.showConfirmation;
  config = this.confirmationService.confirmationConfig;

  readonly baseButtonClass =
    'bg-surface-bg-primary-strong not-disabled:hover:bg-surface-bg-primary-solid text-typography-generic-white font-medium text-sm px-4 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-border-primary-primary shadow-sm disabled:bg-surface-bg-primary-soft disabled:text-typography-primary-active disabled:cursor-not-allowed';

  // Save button specific class using faint background
  readonly saveButtonClass =
    'bg-surface-bg-primary-faint not-disabled:hover:bg-surface-bg-primary-subtle text-typography-primary-primary font-medium text-sm px-4 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-border-primary-primary shadow-sm disabled:bg-surface-bg-primary-soft disabled:text-typography-primary-active disabled:cursor-not-allowed';

  // Stretches every action to the full bar width on compact screens
  readonly compactButtonClass = ' max-lg:w-full max-lg:min-w-0 max-lg:px-3';

  // Handle Next action
  handleNextClick(): void {
    this.confirmationService.onNextClick();
  }

  // Handle Save action
  handleSaveClick(): void {
    this.confirmationService.onSaveClick();
  }

  // Handle Save Edits action
  handleSaveEditsClick(): void {
    this.confirmationService.onSaveEditsClick();
  }

  // Handle Cancel action
  handleCancelClick(): void {
    if (typeof this.confirmationService.onCancelClick === 'function') {
      this.confirmationService.onCancelClick();
    } else {
      // fallback to service method if not mocked
      if (typeof this.confirmationService.onSecondaryClick === 'function') {
        this.confirmationService.onSecondaryClick();
      }
    }
  }

  // Handle Back action
  handleBackClick(): void {
    this.confirmationService.onBackClick();
  }

  handleSecondaryClick(): void {
    this.handleCancelClick();
  }

  // Format form data for display
  formatFormData(formData: Record<string, unknown>): string {
    if (!formData) return '';

    return Object.entries(formData)
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
  }

  /** Convert config title to a slug suitable for use in element IDs */
  titleToId(title: string | undefined): string {
    if (!title) return 'confirmation';
    return title.toLowerCase().replace(/\s+/g, '_');
  }
}
